import React, { createContext, useState, useEffect, useCallback } from 'react'
import { useQuery } from '@apollo/client'
import { reporterQuery } from '../utils/queries'
import { sortDataByProperty, decodingMiddleware } from '../utils/helpers'
import { 
  clientMainnet, clientSepolia, clientMatic, clientAmoy, clientArbone, 
  clientGnosismain, clientOptMain, clientLinea, clientPolygonzk, 
  clientZksyncMain, clientScroll, clientBase
} from '../utils/apollo'

export const GraphContext = createContext()

const explorerUrls = {
  mainnet: 'https://etherscan.io/tx/',
  sepolia: 'https://sepolia.etherscan.io/tx/',
  matic: 'https://polygonscan.com/tx/',
  amoy: 'https://amoy.polygonscan.com/tx/',
  arbone: 'https://arbiscan.io/tx/',
  gnosismain: 'https://gnosisscan.io/tx/',
  optMain: 'https://optimistic.etherscan.io/tx/',
  linea: 'https://lineascan.build/tx/',
  polygonzk: 'https://zkevm.polygonscan.com/tx/',
  zksyncMain: 'https://explorer.zksync.io/tx/',
  scroll: 'https://scrollscan.com/tx/',
  base: 'https://basescan.org/tx/'
};

const networkNames = {
  mainnet: 'Ethereum Mainnet',
  sepolia: 'Sepolia Testnet',
  matic: 'Polygon Mainnet',
  amoy: 'Amoy Testnet',
  arbone: 'Arbitrum Mainnet',
  gnosismain: 'Gnosis Mainnet',
  optMain: 'Optimism Mainnet',
  linea: 'Linea Mainnet',
  polygonzk: 'Polygon zkEVM Main',
  zksyncMain: 'ZkSync Mainnet',
  scroll: 'Scroll Mainnet',
  base: 'Base Mainnet'
};

const Graph = ({ children }) => {
  const [networkData, setNetworkData] = useState({
    mainnet: { data: null, loading: true, error: null },
    sepolia: { data: null, loading: true, error: null },
    matic: { data: null, loading: true, error: null },
    amoy: { data: null, loading: true, error: null },
    arbone: { data: null, loading: true, error: null },
    gnosismain: { data: null, loading: true, error: null },
    optMain: { data: null, loading: true, error: null },
    linea: { data: null, loading: true, error: null },
    polygonzk: { data: null, loading: true, error: null },
    zksyncMain: { data: null, loading: true, error: null },
    scroll: { data: null, loading: true, error: null },
    base: { data: null, loading: true, error: null }
  });
  const [allGraphData, setAllGraphData] = useState([]);
  const [decodedData, setDecodedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterState, setFilterState] = useState({
    symbolFilters: [],
    chainFilters: [],
    reporterFilters: [],
    dateFilters: [],
    startDateSearchTerm: "",
    endDateSearchTerm: ""
  });

  const useQueryWithTimeout = (query, client, networkName, timeout = 10000, pollInterval = 60000) => {
    console.log(`Setting up query for ${networkName} with client:`, client);
    const { data, loading, error } = useQuery(query, {
      client,
      fetchPolicy: 'network-only',
      pollInterval,
    });

    useEffect(() => {
      const timer = setTimeout(() => {
        if (loading) {
          setNetworkData(prev => ({
            ...prev,
            [networkName]: { ...prev[networkName], loading: false, error: new Error('Timeout') }
          }));
        }
      }, timeout);

      return () => clearTimeout(timer);
    }, [loading, networkName, timeout]);

    useEffect(() => {
      if (networkName === 'mainnet') {
        console.log('Mainnet query state:', { data, loading, error });
      }
      setNetworkData(prev => ({
        ...prev,
        [networkName]: { data, loading, error }
      }));
    }, [data, loading, error, networkName]);

    return { data, loading, error };
  };

  // Instead, directly call useQueryWithTimeout for each network
  console.log('Setting up mainnet client:', clientMainnet);
  useQueryWithTimeout(reporterQuery, clientMainnet, 'mainnet', 10000, 5000); // 5 second polling for mainnet
  useQueryWithTimeout(reporterQuery, clientSepolia, 'sepolia', 10000, 60000); // 1 minute polling for others
  useQueryWithTimeout(reporterQuery, clientMatic, 'matic', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientAmoy, 'amoy', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientArbone, 'arbone', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientGnosismain, 'gnosismain', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientOptMain, 'optMain', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientLinea, 'linea', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientPolygonzk, 'polygonzk', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientZksyncMain, 'zksyncMain', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientScroll, 'scroll', 10000, 60000);
  useQueryWithTimeout(reporterQuery, clientBase, 'base', 10000, 60000);

  useEffect(() => {
    const processNetworkData = (networkName, data) => {
      if (!data || !data.newReportEntities) return [];
      
      return data.newReportEntities.map(event => ({
        ...event,
        chain: networkNames[networkName],
        txnLink: `${explorerUrls[networkName]}${event.txnHash}`,
        uniqueId: `${event.txnHash}-${networkName}`
      }));
    };

    // Debug: Log mainnet data specifically
    if (networkData.mainnet && networkData.mainnet.data) {
      console.log('Mainnet data received:', networkData.mainnet.data);
      console.log('Mainnet newReportEntities count:', networkData.mainnet.data.newReportEntities?.length || 0);
    }

    const newData = Object.entries(networkData).flatMap(([networkName, { data }]) => 
      processNetworkData(networkName, data)
    );

    console.log('Total newData processed:', newData.length);
    console.log('Data by network:', Object.entries(networkData).map(([name, { data }]) => ({
      network: name,
      hasData: !!data,
      count: data?.newReportEntities?.length || 0
    })));

    setAllGraphData(prev => {
      const combined = [...prev, ...newData];
      const uniqueData = Array.from(new Map(combined.map(item => [item.uniqueId, item])).values());
      return sortDataByProperty('_time', uniqueData);
    });

  }, [networkData]);

  useEffect(() => {
    if (allGraphData.length > 0) {
      const decoded = decodingMiddleware(allGraphData);
      setDecodedData(decoded);
    }
  }, [allGraphData]);

  // Add filter function at context level
  const applyFilters = useCallback((data, filters) => {
    if (!data) return [];

    return data.filter(event => {
      const symbolMatch = filters.symbolFilters.length === 0 || filters.symbolFilters.includes(event.decodedValueName);
      const chainMatch = filters.chainFilters.length === 0 || filters.chainFilters.includes(event.chain);
      const reporterMatch = filters.reporterFilters.length === 0 || filters.reporterFilters.includes(event.decodedReporter);
      
      let startDate = new Date(filters.startDateSearchTerm);
      let endDate = new Date(filters.endDateSearchTerm);
      let eventDate = new Date(event.decodedTime.split(',')[0].trim().split('/').reverse().join('-'));

      const dateMatch = filters.startDateSearchTerm && filters.endDateSearchTerm ? 
        (eventDate >= startDate && eventDate <= endDate) : 
        filters.dateFilters.length === 0 || filters.dateFilters.some(filterDate => event.decodedTime.startsWith(filterDate));
  
      return symbolMatch && chainMatch && reporterMatch && dateMatch;
    });
  }, []);

  // Apply filters whenever data or filter state changes
  useEffect(() => {
    if (!decodedData.length) return;
    const filtered = applyFilters(decodedData, filterState);
    setFilteredData(filtered);
  }, [decodedData, filterState, applyFilters]);

  return (
    <GraphContext.Provider value={{ allGraphData, decodedData, networkData, filteredData, filterState }}>
      {children}
    </GraphContext.Provider>
  )
}

export default Graph